import * as React from "react";

import { StyledInput } from "src/core/shared/components/InputFields";
import { Modal } from "src/library/components/Modal";
import { Text } from "src/library/components/Text";
import { ITerminology } from "src/library/utils";

/**
 * @description Props for the add invoice number modal.
 */
interface AddInvoiceNumberModalProps {
  /** Callback to close the modal */
  closeModal: () => void;
  /** Controls the show state of the modal */
  show: boolean;
  /** Controls client specific terminologies */
  terminology: ITerminology;
  /** Callback to fire on add invoice number */
  onConfirm: (invoiceNumber: string) => void;
  /** Controls loading state of the modal */
  loading: boolean;
  /** Initial value with which to populate the input field */
  initialValue?: string;
}

/**
 * @description Modal allowing an invoice number to be added to an order.
 */
export const AddInvoiceNumberModal: React.FC<AddInvoiceNumberModalProps> = ({
  closeModal, show, terminology, onConfirm, loading, initialValue
}) => {
  const [ invoiceNumber, setInvoiceNumber ] = React.useState<string>(initialValue || "");
  const [ invoiceNumberError, setInvoiceNumberError ] = React.useState<string | null>(null);

  // Handles adding of the invoice number input to the order.
  const handleAddInvoiceNumber = React.useCallback(() => {
    if (invoiceNumber.length === 0) {
      return setInvoiceNumberError("Invoice number required.");
    }

    onConfirm(invoiceNumber);
    setInvoiceNumberError(null);
  }, [ invoiceNumber, onConfirm ]);

  return (
    <Modal
      small
      actions={[
        {
          name: "Go back",
          action: () => {
            closeModal();
          },
          $buttonType: "secondary"
        },
        {
          name: "Confirm",
          action: () => {
            handleAddInvoiceNumber();
          },
          disabled: loading,
          $buttonType: "primary"
        }

      ]}
      content={(
        <>
          <StyledInput
            onChange={e => {
              const elImputValue = (e.target.value).trim();

              setInvoiceNumber(elImputValue);
            }}
            value={invoiceNumber}
            placeholder="Invoice number"
          />

          {invoiceNumberError && (
            <Text color="error">
              {invoiceNumberError}
            </Text>
          )}
        </>
      )}
      show={show}
      title={`Add invoice number to ${terminology.order.singular.text}`}
      backgroundAction={() => closeModal()}
      loading={loading}
    />
  );
};